export interface Stage {
  number: string
  title: string
  description: string
  tags: string[]
}

export interface Bonus {
  number: string
  title: string
  promise: string
  description: string
  value: number
  cover: string
}

export interface Testimonial {
  initials: string
  quote: string
  name: string
  role: string
}

export interface FaqItem {
  question: string
  answer: string
}

export const PRICE = 25
export const TOTAL_VALUE = 292
export const SAVINGS = 267
export const BONUS_VALUE = 195

export const CHECKOUT_URL =
  'https://the-perfect-dog-method.impultienda.ar/checkout'

export const BUNDLE_MOCKUP =
  'https://cdn.magicpatterns.com/uploads/v9gADdUSjDb4YMCCk1SXCX/ChatGPT_Image_28_ago_2026,_03_25_55_p.m..png'

export const heroBadges = [
  '30-day training system',
  '5 free bonuses',
  '15 minutes a day',
  'Any age, breed or history',
  'Instant digital access',
]

export const heroStats = [
  { value: '30 days', label: 'From chaos to a calm, obedient companion' },
  { value: '1 + 5', label: 'Main program and bonuses included' },
  { value: '15 min', label: 'A day is all the system asks of you' },
  { value: '48 hours', label: 'To your first visible wins' },
]

export const heroQuotes: Testimonial[] = [
  {
    initials: 'LM',
    quote:
      'Our walks used to wreck my shoulder. By the end of week one Milo was walking beside me instead of dragging me down the street.',
    name: 'Laura M.',
    role: 'Owner of a 3-year-old rescue',
  },
  {
    initials: 'DR',
    quote:
      'I had tried videos, collars, and yelling. The difference here is the day-by-day order — I finally knew what to do each morning.',
    name: 'Daniel R.',
    role: 'Owner of two labradors',
  },
  {
    initials: 'PC',
    quote:
      'Guests can knock on the door now without the barking meltdown. Fifteen minutes a day was genuinely all it took.',
    name: 'Priya C.',
    role: 'First-time dog owner',
  },
]

export const problems = [
  'Walks that leave your shoulder aching from constant pulling',
  'Embarrassing barking every single time guests arrive',
  'Being completely ignored every time you call their name',
  'Random YouTube videos that all contradict each other',
  'Vibrating collars that treat the symptom and never the cause',
  'Yelling “NO” until you are blue in the face — and nothing changes',
]

export const systemMetrics = [
  { value: '30', label: 'Day-by-day roadmap' },
  { value: '15', label: 'Minutes a day' },
  { value: '48', label: 'Hours to first results' },
  { value: '6', label: 'Guides in the bundle' },
]

export const coverage = [
  { number: '01', label: 'Clarity & Consistency Basics' },
  { number: '02', label: 'Loose-Leash Walking' },
  { number: '03', label: 'Barking & Alert Triggers' },
  { number: '04', label: 'Reliable Recall' },
  { number: '05', label: 'Calm Home & Guests' },
  { number: '06', label: 'Body Language & Prevention' },
]

export const stages: Stage[] = [
  {
    number: '01',
    title: 'Days 1–5 · Clarity Foundations',
    description:
      'Replace guesswork with a routine your dog can read. Set the markers, rewards, and timing that make every later step land.',
    tags: ['Marker Training', 'Timing', 'Daily Routine'],
  },
  {
    number: '02',
    title: 'Days 6–10 · Attention & Name',
    description:
      'End the feeling of being ignored. Your dog learns that their name always means “look at me”, even with distractions around.',
    tags: ['Name Response', 'Eye Contact', 'Distraction Work'],
  },
  {
    number: '03',
    title: 'Days 11–15 · Loose-Leash Walking',
    description:
      'Stop the sled-dog pulling. Short daily drills turn walks from a shoulder workout into the calmest part of your day.',
    tags: ['Pressure Off', 'Turns', 'Real-World Walks'],
  },
  {
    number: '04',
    title: 'Days 16–20 · Quiet On Cue',
    description:
      'Turn off the alert trigger without yelling. You learn why the barking starts and how to interrupt it before it escalates.',
    tags: ['Alert Triggers', 'Quiet Cue', 'Doorbell Drills'],
  },
  {
    number: '05',
    title: 'Days 21–25 · Recall & Calm Home',
    description:
      'Your dog abandons the distraction and comes running. Jumping on guests, chewing, and door chaos get their own protocol.',
    tags: ['Recall Games', 'Guest Protocol', 'Settle Work'],
  },
  {
    number: '06',
    title: 'Days 26–30 · Lock It In',
    description:
      'Habits become default behavior. Proofing sessions plus a maintenance plan so the calm dog you built stays that way.',
    tags: ['Proofing', 'Maintenance', 'Progress Check'],
  },
]

export const bonuses: Bonus[] = [
  {
    number: '01',
    title: 'The Leash Walking Blueprint',
    promise: 'Stop the “sled-dog” behavior.',
    description:
      'Transform walks from a chore into a relaxing experience where your dog stays politely by your side.',
    value: 47,
    cover:
      'https://cdn.magicpatterns.com/uploads/hWr52f2sjbqordpd2PhpmK/mascota_1.webp',
  },
  {
    number: '02',
    title: 'The Barking Solution',
    promise: 'Silence the noise.',
    description:
      'Learn exactly why your dog barks and how to turn off the “alert” trigger instantly without yelling.',
    value: 37,
    cover:
      'https://cdn.magicpatterns.com/uploads/tf9QY5cbogH5ApVf15Epry/mascota_2.webp',
  },
  {
    number: '03',
    title: 'The Perfect Recall System',
    promise: 'The ultimate peace of mind.',
    description:
      'Teach your dog to abandon any distraction and come running back to you every single time you call.',
    value: 47,
    cover:
      'https://cdn.magicpatterns.com/uploads/6QcuvMyrP1E7PyoCw77ejU/mascota_3.webp',
  },
  {
    number: '04',
    title: 'The Calm Home Protocol',
    promise: 'End the indoor chaos.',
    description:
      'Stop the jumping on guests, the destructive chewing, and the overexcitement the moment you walk through the door.',
    value: 37,
    cover:
      'https://cdn.magicpatterns.com/uploads/jvXmN87yNL5UQEH63dJhZz/mascota_4.webp',
  },
  {
    number: '05',
    title: 'The Dog Body Language Bible',
    promise: 'Become a “Dog Whisperer.”',
    description:
      'Understand exactly what your dog is trying to tell you before they react, so you can prevent problems before they start.',
    value: 27,
    cover:
      'https://cdn.magicpatterns.com/uploads/o79f17M16TPPY1mFoSyQVH/mascota_5.webp',
  },
]

export const includedItems = [
  'The Perfect Dog 30-Day System ($97)',
  'Bonus #1: Leash Walking Blueprint ($47)',
  'Bonus #2: The Barking Solution ($37)',
  'Bonus #3: The Perfect Recall System ($47)',
  'Bonus #4: The Calm Home Protocol ($37)',
  'Bonus #5: Dog Body Language Bible ($27)',
]

export const testimonials: Testimonial[] = [
  {
    initials: 'LM',
    quote:
      'Our walks used to wreck my shoulder and I dreaded them. Three weeks in, Milo walks beside me on a loose leash and I actually look forward to going out.',
    name: 'Laura M.',
    role: 'Owner of a 3-year-old rescue',
  },
  {
    initials: 'DR',
    quote:
      'The difference is the day-by-day order. I stopped guessing and just did the 15 minutes. First real change showed up on day two.',
    name: 'Daniel R.',
    role: 'Owner of two labradors',
  },
  {
    initials: 'PC',
    quote:
      'Guests can knock on the door now without a barking meltdown. The Calm Home Protocol alone was worth the price.',
    name: 'Priya C.',
    role: 'First-time dog owner',
  },
  {
    initials: 'TW',
    quote:
      'My 9-year-old beagle finally comes when called. I was told old dogs could not change — that was wrong.',
    name: 'Tom W.',
    role: 'Owner of a senior beagle',
  },
  {
    initials: 'SK',
    quote:
      'I work long days and had zero time for a trainer. Fifteen minutes a night fit into my life, and my dog is a different animal.',
    name: 'Sara K.',
    role: 'Busy professional, one husky',
  },
]

export const faqs: FaqItem[] = [
  {
    question: 'My dog is older, can they still learn?',
    answer:
      'Absolutely. Old dogs can learn new tricks. This system works for dogs of any age, breed, or history.',
  },
  {
    question: "I don't have much time. Is this for me?",
    answer:
      'Yes! We designed this for busy people. You only need 15 minutes a day to see real results.',
  },
  {
    question: 'How soon will I get access?',
    answer:
      'Immediately. As soon as you complete your purchase, you will receive a link to download the entire bundle.',
  },
]

export const comparison = [
  {
    without: 'Walks that leave your shoulder aching',
    with: 'Loose-leash walks you actually enjoy',
  },
  {
    without: 'Barking chaos every time guests arrive',
    with: 'A quiet cue that works without yelling',
  },
  {
    without: 'Your dog ignores their own name',
    with: 'Reliable recall from any distraction',
  },
  {
    without: 'Random videos and contradicting advice',
    with: 'One day-by-day 30-day sequence',
  },
  {
    without: 'Hours of guessing and growing stress',
    with: '15 minutes a day, results in 48 hours',
  },
]

export const guaranteePoints = [
  'Simple email refund',
  'No forms to fill',
  'Keep the program anyway',
]
